// Text normalization utility: remove diacritics, lowercase, trim
export const normalize = (s = '') =>
  s
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

// Build a unique suggestions list from show titles and venue names
export const buildSuggestions = (shows = []) => {
  const nameSet = new Set();
  const venueSet = new Set();
  shows.forEach((show) => {
    const name = show?.fullname;
    const venue = show?.venue;
    if (name) nameSet.add(name);
    if (venue) venueSet.add(venue);
  });

  const nameEntries = [...nameSet].map((label) => ({ label, isVenue: false }));
  const venueEntries = [...venueSet].map((label) => ({ label, isVenue: true }));

  return [...nameEntries, ...venueEntries].sort((a, b) => a.label.localeCompare(b.label, 'es'));
};

const getShowTags = (show) => {
  if (Array.isArray(show?.tags) && show.tags.length) return show.tags;
  return show?.genre ? [show.genre] : [];
};

// Filter shows by query and tags; excludes VOD items by default
export const filterShows = (shows = [], query = '', selectedTags = []) => {
  const q = normalize(query);
  const selectedSet = new Set(selectedTags || []);

  const filterByText = (s) => {
    if (!q) return true;
    return [s?.fullname, s?.venue].some((v) => v && normalize(v).includes(q));
  };

  const filterByTags = (s) => {
    if (!selectedSet.size) return true;
    const tags = getShowTags(s).map((t) => normalize(t));
    for (const t of selectedSet) {
      if (tags.includes(normalize(t))) return true; // ANY-of-selected
    }
    return false;
  };

  return shows
    .filter((s) => !s?.settings?.vod)
    .filter((s) => filterByText(s) && filterByTags(s));
};

// Group shows into Esta semana, Próximos eventos, and future months
export const groupShows = (shows = [], now = new Date()) => {
  const current = new Date(now);
  const dayIndex = (current.getDay() + 6) % 7; // Monday = 0

  const weekStart = new Date(current);
  weekStart.setDate(current.getDate() - dayIndex);
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  weekEnd.setHours(23, 59, 59, 999);

  const inFuture = shows
    .filter((s) => new Date(s.show_date) >= now)
    .sort((a, b) => new Date(a.show_date) - new Date(b.show_date));

  const week = [];
  const month = [];
  const futureByMonth = new Map();

  inFuture.forEach((s) => {
    const sd = new Date(s.show_date);
    const isSameMonth =
      sd.getFullYear() === current.getFullYear() && sd.getMonth() === current.getMonth();

    if (sd >= weekStart && sd <= weekEnd) {
      week.push(s);
    } else if (isSameMonth) {
      month.push(s);
    } else {
      const keyRaw = sd.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      const key = keyRaw.charAt(0).toUpperCase() + keyRaw.slice(1);
      if (!futureByMonth.has(key)) futureByMonth.set(key, []);
      futureByMonth.get(key).push(s);
    }
  });

  return { week, month, futureByMonth };
};

export const extractAllTags = (shows = []) => {
  const set = new Set();
  shows.forEach((s) => {
    const tags = getShowTags(s);
    tags.forEach((t) => {
      if (t && t.toString().trim()) set.add(t);
    });
  });
  return [...set];
};


